/**
 * "Scrivi a piu' persone": resoconto per destinatario.
 *
 * L'esito si legge in due posti (RPC whatsapp_bulk_send_report):
 *  - whatsapp_scheduled_messages: 'scheduled' = in attesa, 'cancelled' =
 *    annullato, 'failed' = non partito (motivo gia' in italiano), 'sent' =
 *    consegnato al core;
 *  - per il Numero Standard, dopo 'sent', la riga di whatsapp_queue dice se
 *    WAHA l'ha spedito davvero ('sent') o no ('failed'/'cancelled').
 * Per il Numero Ufficiale 'sent' vuol dire gia' accettato da Meta.
 *
 * Modulo puro, testato in __tests__/bulkReport.test.ts.
 */
import type { BulkChannel } from './bulkSchedule';

export type BulkOutcome = 'in_attesa' | 'inviato' | 'fallito' | 'annullato';

export const OUTCOME_LABELS: Record<BulkOutcome, string> = {
  in_attesa: 'In attesa',
  inviato: 'Inviato',
  fallito: 'Non inviato',
  annullato: 'Annullato',
};

export interface BulkReportRow {
  scheduled_id: string;
  recipient_name: string | null;
  chat_jid: string;
  client_id: string | null;
  scheduled_at: string;
  scheduled_status: string | null;
  scheduled_error: string | null;
  queue_status: string | null;
  queue_error: string | null;
  sent_at: string | null;
}

/** Motivo leggibile per un errore della coda WAHA (spesso tecnico, in inglese). */
export function friendlyQueueError(raw: string | null | undefined): string {
  const e = (raw || '').toLowerCase();
  if (!e) return 'Invio non riuscito.';
  if (/not (exist|registered|on whatsapp)|no lid|invalid (jid|number|wid)/.test(e)) return 'Il numero non risulta su WhatsApp.';
  if (/session|not (working|connected)|scan|qr|failed to (start|connect)/.test(e)) return 'Il numero aziendale non era collegato al momento dell\'invio.';
  if (/rate|too many|429/.test(e)) return 'Troppi messaggi in poco tempo: invio fermato per proteggere il numero.';
  if (/timeout|timed out|econn|network/.test(e)) return 'WhatsApp non ha risposto in tempo.';
  if (/cancel/.test(e)) return 'Invio annullato.';
  return 'Invio non riuscito.';
}

export function outcomeOf(row: BulkReportRow, channel: BulkChannel): { outcome: BulkOutcome; reason: string | null } {
  switch (row.scheduled_status) {
    case 'scheduled':
      return { outcome: 'in_attesa', reason: null };
    case 'cancelled':
      return { outcome: 'annullato', reason: null };
    case 'failed':
      return { outcome: 'fallito', reason: row.scheduled_error || 'Invio non riuscito.' };
    case 'sent':
      break;
    default:
      return { outcome: 'in_attesa', reason: null };
  }
  if (channel === 'ufficiale') return { outcome: 'inviato', reason: null };
  switch (row.queue_status) {
    case 'sent':
      return { outcome: 'inviato', reason: null };
    case 'failed':
    case 'cancelled':
      return { outcome: 'fallito', reason: friendlyQueueError(row.queue_error) };
    default:
      // In coda (pending/processing) o appena consegnato: il core lo sta spedendo.
      return { outcome: 'in_attesa', reason: null };
  }
}

export type BulkSummary = Record<BulkOutcome, number> & { totale: number };

export function summarize(rows: BulkReportRow[], channel: BulkChannel): BulkSummary {
  const s: BulkSummary = { in_attesa: 0, inviato: 0, fallito: 0, annullato: 0, totale: rows.length };
  for (const r of rows) s[outcomeOf(r, channel).outcome]++;
  return s;
}

/** Frase breve per l'elenco degli invii. */
export function summaryLine(s: BulkSummary): string {
  const parts = [`${s.inviato} inviati`];
  if (s.fallito) parts.push(`${s.fallito} non inviati`);
  if (s.in_attesa) parts.push(`${s.in_attesa} in attesa`);
  if (s.annullato) parts.push(`${s.annullato} annullati`);
  return `${parts.join(', ')} su ${s.totale}`;
}
